import { css } from '@emotion/react';
import styled from '@emotion/styled';
import clsx from 'clsx';

import { orderedBreakpointKeys, screen } from '@/components/common/breakpoints';
import { Icon } from '@/components/common/Icon';
import { Title } from '@/components/common/Title/Title';
import {
  iconProperties,
  iconSize,
  iconSpacing,
} from '@/components/common/TitleWithIcon/helpers';
import { Stack } from '@/components/layout/Stack';

import type { IconSizesSet, StrokeWidthSet } from '@/components/common/Icon';
import type { TitleProps } from '@/components/common/Title/types';
import type {
  TitleWithIconComponentVariants,
  TitleWithIconIconVariants,
  TitleWithIconIconVariantsSet,
  TitleWithIconSizeOptions,
} from '@/components/common/TitleWithIcon/helpers';

export interface TitleWithIconProps
  extends Omit<
    TitleProps,
    | 'variant'
    | 'size'
    | 'hidden'
    | 'badge'
    | 'badgeColour'
    | 'caption'
    | 'smallSubheading'
    | 'desktopAlignment'
    | 'mobileAlignment'
  > {
  iconName?: string;
  iconVariant?: TitleWithIconIconVariants | TitleWithIconIconVariantsSet;
  variant?: TitleWithIconComponentVariants;
  size?: TitleWithIconSizeOptions;
  alignment?: 'top' | 'left' | 'center';
  mobileAlignment?: 'inherit' | 'top' | 'left' | 'center';
  className?: string;
}

export const TitleWithIcon = ({
  iconName,
  iconVariant = 'standard',
  variant = 'component',
  size = 'lg',
  alignment = 'left',
  mobileAlignment = 'inherit',
  heading,
  subheading,
  className,
}: TitleWithIconProps) => {
  const titleAlignment = alignment == 'center' ? 'center' : 'start';
  return (
    <TitleWithIconContainer
      spacing={iconSpacing(size, variant)}
      alignment={alignment}
      mobileAlignment={mobileAlignment}
      className={clsx(className, 'title-with-icon')}
    >
      {iconName && (
        <Icon
          icon={iconName}
          {...iconConfig(size, variant, iconVariant)}
          illustrative
        />
      )}
      <Title
        size={size}
        variant={variant}
        desktopAlignment={titleAlignment}
        mobileAlignment={titleAlignment}
        heading={heading}
        subheading={subheading}
      />
    </TitleWithIconContainer>
  );
};

const iconConfig = (
  size: TitleWithIconSizeOptions,
  variant: TitleWithIconComponentVariants,
  iconVariant: TitleWithIconIconVariants | TitleWithIconIconVariantsSet,
) => {
  if (typeof iconVariant === 'string') {
    const iconLetterSize = iconSize(size, variant, iconVariant);
    return {
      size: iconProperties[iconLetterSize].size,
      strokeWidth: iconProperties[iconLetterSize].strokeWidth,
    };
  } else {
    const iconSizesSet: IconSizesSet = {};
    const strokeWidthSet: StrokeWidthSet = {};

    orderedBreakpointKeys.forEach((breakpoint) => {
      if (Object.keys(iconVariant).includes(breakpoint)) {
        const iconLetterSize = iconSize(size, variant, iconVariant[breakpoint]);
        iconSizesSet[breakpoint] = iconProperties[iconLetterSize].size;
        strokeWidthSet[breakpoint] = iconProperties[iconLetterSize].strokeWidth;
      }
    });

    return {
      size: iconSizesSet,
      strokeWidth: strokeWidthSet,
    };
  }
};

const TitleWithIconContainer = styled(Stack)<{
  alignment: 'top' | 'left' | 'center';
  mobileAlignment?: 'inherit' | 'top' | 'left' | 'center';
}>`
  display: flex;

  ${(props) => {
    const alignment =
      props.mobileAlignment === 'inherit'
        ? props.alignment
        : props.mobileAlignment;
    return css`
      flex-direction: ${alignment === 'left' ? 'row' : 'column'};
      align-items: ${alignment === 'center' ? 'center' : 'start'};
    `;
  }}

  ${screen.sm} {
    ${(props) => {
      return css`
        flex-direction: ${props.alignment === 'left' ? 'row' : 'column'};
        align-items: ${props.alignment === 'center' ? 'center' : 'start'};
      `;
    }}
  }
`;
